import {
  CalculateOfferRequest,
  CalculateOfferResponse,
  ConfirmSeatsPlanRequest,
  ConfirmSeatsPlanResponse,
  ConfirmedOrderModel,
  CreateAppleSessionRequest,
  CreateAppleSessionResponse,
  GetOrderByIdResponse,
  InitOrderRequest,
  InitOrderResponse,
  PayOrderGuestBookingRequest,
  PayOrderRequest,
  PayOrderResponse,
  SeatsPlanResponse,
  SessionResponse,
  ShareSeatsPlanRequest,
  ShareSeatsPlanResponse,
  ValidateAppleSessionRequest,
  ValidateAppleSessionResponse,
} from './models';
import { createMutation, createQuery } from '../../libs';

const sessionsUrl = 'showtimes';
export const ordersUrl = 'orders';
const offersUrl = 'offers';
const guestOrdersUrl = 'guest-users/orders';
const ApplePayCardsUrl = 'cards';

/** Payment authorization is slower than the 9s global axios timeout allows. */
const PAY_REQUEST_TIMEOUT = 60000;

export const useInitOrder = createMutation<InitOrderResponse, InitOrderRequest>({
  method: 'post',
  url: ordersUrl,
});

export const useGetSession = createQuery<SessionResponse>({
  method: 'get',
  url: `${sessionsUrl}/:sessionId`,
});

export const useGetOrderById = createQuery<GetOrderByIdResponse>({
  method: 'get',
  url: `${ordersUrl}/:id`,
});

export const useGuestBookingGetOrderById = createQuery<GetOrderByIdResponse>({
  method: 'get',
  url: `${guestOrdersUrl}/:id`,
  transformRequest: (data, headers) => {
    headers['Content-Type'] = 'application/json';
    delete headers.Authorization;
    return JSON.stringify(data);
  },
});

export const useGetSeatsPlan = createQuery<SeatsPlanResponse>({
  method: 'get',
  url: `${sessionsUrl}/:sessionId/seats`,
});

export const useShareSeatsPlan = createMutation<
  ShareSeatsPlanResponse,
  ShareSeatsPlanRequest
>({
  method: 'post',
  url: `${sessionsUrl}/:sessionId/share-seats`,
});

export const useCalculateOffer = createMutation<
  CalculateOfferResponse,
  CalculateOfferRequest
>({
  method: 'post',
  url: `${offersUrl}/:offerId/calculate`,
});

export const useConfirmSeatsPlan = createMutation<
  ConfirmSeatsPlanResponse,
  ConfirmSeatsPlanRequest
>({
  method: 'post',
  url: `${ordersUrl}/:orderId/seats`,
});

export const usePayOrder = createMutation<
  PayOrderResponse,
  PayOrderRequest,
  'id',
  unknown
>({
  method: 'post',
  // Payment authorization round-trips through Vista + the payment gateway and
  // regularly exceeds the 9s global axios timeout, which aborts the request and
  // surfaces as a silently cancelled /pay. Override it for the pay call only.
  timeout: PAY_REQUEST_TIMEOUT,
  url: `${ordersUrl}/:id/pay`,
});

export const useGuestBookingPayOrder = createMutation<
  PayOrderResponse,
  PayOrderGuestBookingRequest,
  'id',
  unknown
>({
  method: 'post',
  timeout: PAY_REQUEST_TIMEOUT,
  url: `${guestOrdersUrl}/:id/pay`,
  // this function to remove Authorization in case logged-in user and incomplete profile
  transformRequest: (data, headers) => {
    headers['Content-Type'] = 'application/json';
    delete headers.Authorization;
    return JSON.stringify(data);
  },
});

export const useDeleteOffer = createMutation<
  { order: ConfirmedOrderModel },
  unknown,
  'id'
>({
  method: 'delete',
  url: `${ordersUrl}/:id/offers`,
});

// TODO: add types to the following service
export const useCheckCard = createMutation<
  unknown,
  unknown,
  'id',
  {
    cardId: string;
    bin: string;
  }
>({
  method: 'get',
  url: `${ordersUrl}/:id/offers/check-card`,
});

export const useValidateAppleSessionMutation = createMutation<
  ValidateAppleSessionResponse,
  ValidateAppleSessionRequest
>({
  method: 'post',
  url: `${ApplePayCardsUrl}/validate-apple-session`,
});
export const useCreateAppleSessionMutation = createMutation<
  CreateAppleSessionResponse,
  CreateAppleSessionRequest
>({
  method: 'post',
  url: `${ApplePayCardsUrl}/create-apple-session`,
});
